import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Plus, Trash2, Printer, Save, CreditCard, Upload } from "lucide-react";
import { toast } from "sonner";

const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const statusStyle = {
  Paid: "bg-green-500/12 text-green-400", Partial: "bg-amber-500/12 text-amber-400",
  Unpaid: "bg-red-500/12 text-red-400", Draft: "bg-zinc-500/20 text-zinc-400",
};

export default function InvoiceEditor({ api, invoiceId, module, allowBranding, onClose, onSaved }) {
  const [inv, setInv] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [pay, setPay] = useState({ amount: "", method: "Cash", date: new Date().toISOString().slice(0, 10) });

  useEffect(() => {
    if (!invoiceId) { setInv(null); return; }
    api.get(`/invoices/${invoiceId}`).then(({ data }) => setInv(data)).catch(() => toast.error("Failed to load invoice"));
  }, [invoiceId, api]);

  if (!invoiceId) return null;
  const set = (k, v) => setInv((o) => ({ ...o, [k]: v }));
  const setItem = (i, k, v) => setInv((o) => { const items = [...o.items]; items[i] = { ...items[i], [k]: v }; return { ...o, items }; });
  const addItem = () => setInv((o) => ({ ...o, items: [...(o.items || []), { description: "", quantity: 1, unit_price: 0 }] }));
  const rmItem = (i) => setInv((o) => ({ ...o, items: o.items.filter((_, x) => x !== i) }));

  const subtotal = (inv?.items || []).reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);
  const tax = inv?.tax_applied ? subtotal * 0.13 : 0;
  const total = subtotal + tax;
  const paid = (inv?.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
  const isPaidFlag = !!inv?.manual_paid;
  const shownPaid = isPaidFlag ? total : paid;
  const balance = isPaidFlag ? 0 : total - paid;
  const displayStatus = isPaidFlag ? "Paid" : inv?.status;

  const payload = () => ({
    invoice_number: inv.invoice_number, source_type: inv.source_type, source_id: inv.source_id,
    issue_date: inv.issue_date, due_date: inv.due_date || "",
    business_name: inv.business_name, business_logo_url: inv.business_logo_url, business_address: inv.business_address,
    business_phone: inv.business_phone, business_email: inv.business_email,
    customer_name: inv.customer_name, customer_email: inv.customer_email, customer_phone: inv.customer_phone, customer_address: inv.customer_address,
    items: (inv.items || []).map((i) => ({ description: i.description, quantity: Number(i.quantity || 0), unit_price: Number(i.unit_price || 0) })),
    notes: inv.notes || "", tax_applied: !!inv.tax_applied, tax_rate: 0.13,
    manual_status: inv.manual_status || "", manual_paid: !!inv.manual_paid, payments: inv.payments || [],
  });

  const save = async (silent) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/invoices/${invoiceId}`, payload());
      setInv(data); onSaved && onSaved();
      if (!silent) toast.success("Invoice saved");
      return data;
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const togglePaid = async (v) => {
    set("manual_paid", v);
    try {
      await api.post(`/invoices/${invoiceId}/mark-paid`, { paid: v });
      onSaved && onSaved();
      toast.success(v ? "Marked as paid" : "Reopened as unpaid");
    } catch { toast.error("Failed to update"); set("manual_paid", !v); }
  };

  const aiFill = async () => {
    if (!aiText.trim()) { toast.error("Describe what to include first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post(`/invoices/ai-fill`, { instructions: aiText, context: inv.ai_context });
      setInv((o) => ({ ...o, items: [...(o.items || []).filter((i) => i.description || i.unit_price), ...data.items], notes: o.notes || data.notes || "" }));
      toast.success("AI added line items — review & edit");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI fill failed"); } finally { setAiBusy(false); }
  };

  const addPayment = async () => {
    if (!Number(pay.amount)) { toast.error("Enter a payment amount"); return; }
    await save(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/payments`, { amount: Number(pay.amount), method: pay.method, date: pay.date });
      setInv(data); setPay({ amount: "", method: "Cash", date: new Date().toISOString().slice(0, 10) }); onSaved && onSaved();
      toast.success("Payment recorded");
    } catch { toast.error("Failed to record payment"); }
  };

  const rmPayment = async (pid) => {
    try { const { data } = await api.delete(`/invoices/${invoiceId}/payments/${pid}`); setInv(data); onSaved && onSaved(); }
    catch { toast.error("Failed to remove payment"); }
  };

  const print = async () => {
    await save(true);
    try {
      const { data } = await api.get(`/invoices/${invoiceId}/html`);
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const html = (data.html || "").replaceAll('src="/api/', `src="${backend}/api/`);
      const iframe = document.createElement("iframe");
      iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
      iframe.srcdoc = html;
      iframe.onload = () => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { console.error(e); } finally { setTimeout(() => iframe.remove(), 1500); } };
      document.body.appendChild(iframe);
    } catch { toast.error("Failed to generate document"); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/invoices/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("business_logo_url", data.url); toast.success("Logo uploaded — save to apply"); }
    catch { toast.error("Logo upload failed"); }
  };

  if (!inv) return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-md" data-testid="invoice-editor-loading"><DialogHeader><DialogTitle className="sr-only">Loading invoice</DialogTitle><DialogDescription className="sr-only">Please wait</DialogDescription></DialogHeader><p className="py-8 text-center text-muted-foreground">Loading invoice…</p></DialogContent></Dialog>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="invoice-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span className="font-heading text-2xl">Invoice {inv.invoice_number}</span>
            <Badge className={`border-0 ${statusStyle[displayStatus]}`} data-testid="invoice-status-badge">{displayStatus}</Badge>
            <span className="ml-auto text-sm font-normal text-muted-foreground">Balance Due <span className="font-bold text-foreground" data-testid="invoice-balance">{money(balance)}</span></span>
          </DialogTitle>
          <DialogDescription className="sr-only">Edit invoice line items, record payments, and print or email the invoice.</DialogDescription>
        </DialogHeader>

        {/* Meta + Bill To */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Invoice #</Label><Input value={inv.invoice_number || ""} onChange={(e) => set("invoice_number", e.target.value)} data-testid="invoice-number-input" /></div>
              <div><Label className="text-xs">Issue Date</Label><Input type="date" value={inv.issue_date || ""} onChange={(e) => set("issue_date", e.target.value)} /></div>
            </div>
            <div><Label className="text-xs">Due Date</Label><Input type="date" value={inv.due_date || ""} onChange={(e) => set("due_date", e.target.value)} /></div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bill To</Label>
            <Input placeholder="Customer name" value={inv.customer_name || ""} onChange={(e) => set("customer_name", e.target.value)} data-testid="invoice-customer-name" />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Email" value={inv.customer_email || ""} onChange={(e) => set("customer_email", e.target.value)} data-testid="invoice-customer-email" />
              <Input placeholder="Phone" value={inv.customer_phone || ""} onChange={(e) => set("customer_phone", e.target.value)} />
            </div>
          </div>
        </div>

        {allowBranding && (
          <div className="border border-border rounded-md p-3 space-y-2 bg-muted/30">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Business branding (this invoice)</Label>
            <div className="flex items-center gap-2 flex-wrap">
              <Input className="flex-1 min-w-[180px]" placeholder="Business name" value={inv.business_name || ""} onChange={(e) => set("business_name", e.target.value)} data-testid="invoice-business-name" />
              <label className="inline-flex items-center gap-2 text-sm px-3 py-2 border border-border rounded-md cursor-pointer hover:bg-accent" data-testid="invoice-logo-upload">
                <Upload className="h-4 w-4" /> {inv.business_logo_url ? "Change logo" : "Upload logo"}
                <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
              </label>
            </div>
          </div>
        )}

        {/* AI fill */}
        <div className="border border-primary/30 rounded-md p-3 space-y-2 bg-primary/5">
          <Label className="text-xs flex items-center gap-1.5 font-semibold"><Sparkles className="h-3.5 w-3.5 text-primary" /> Describe what to include — AI will fill the line items</Label>
          <div className="flex gap-2">
            <Textarea rows={2} className="flex-1" placeholder="e.g. Full synthetic oil change, replace front brake pads, tire rotation" value={aiText} onChange={(e) => setAiText(e.target.value)} data-testid="invoice-ai-input" />
            <Button onClick={aiFill} disabled={aiBusy} className="self-stretch" data-testid="invoice-ai-generate"><Sparkles className="h-4 w-4 mr-1.5" />{aiBusy ? "…" : "Generate"}</Button>
          </div>
        </div>

        {/* Line items */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Line Items</Label>
            <Button size="sm" variant="outline" onClick={addItem} data-testid="invoice-add-item"><Plus className="h-3 w-3 mr-1" />Add line</Button>
          </div>
          <div className="space-y-2">
            {(inv.items || []).map((it, i) => (
              <div key={i} className="flex items-start gap-2" data-testid={`invoice-item-${i}`}>
                <Textarea rows={1} className="flex-1 min-h-[40px]" placeholder="Description" value={it.description || ""} onChange={(e) => setItem(i, "description", e.target.value)} />
                <Input className="w-16" type="number" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} />
                <Input className="w-28" type="number" placeholder="Unit $" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} />
                <div className="w-24 text-right pt-2 font-mono text-sm">{money(Number(it.quantity || 0) * Number(it.unit_price || 0))}</div>
                <Button size="icon" variant="ghost" onClick={() => rmItem(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            ))}
            {!(inv.items || []).length && <p className="text-sm text-muted-foreground py-2">No line items yet — add one or use AI.</p>}
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-between items-start gap-6 flex-wrap">
          <div className="flex flex-col gap-3 pt-2">
            <div className="flex items-center gap-2">
              <Switch checked={!!inv.tax_applied} onCheckedChange={(v) => set("tax_applied", v)} data-testid="invoice-tax-toggle" />
              <span className="text-sm">Apply HST (13%)</span>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2 bg-muted/30" data-testid="invoice-mark-paid-row">
              <Switch checked={isPaidFlag} onCheckedChange={togglePaid} data-testid="invoice-mark-paid-toggle" />
              <span className="text-sm font-medium">Mark invoice as Paid</span>
              {isPaidFlag && <Badge className="border-0 bg-green-500/12 text-green-400 ml-1">PAID</Badge>}
            </div>
          </div>
          <div className="w-64 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{money(subtotal)}</span></div>
            {inv.tax_applied && <div className="flex justify-between"><span className="text-muted-foreground">HST (13%)</span><span className="font-mono">{money(tax)}</span></div>}
            <div className="flex justify-between font-semibold"><span>Total</span><span className="font-mono" data-testid="invoice-total">{money(total)}</span></div>
            {shownPaid > 0 && <div className="flex justify-between text-muted-foreground"><span>Paid</span><span className="font-mono">- {money(shownPaid)}</span></div>}
            <div className="flex justify-between border-t border-border pt-1 font-bold"><span>Balance Due</span><span className="font-mono">{money(balance)}</span></div>
          </div>
        </div>

        {/* Payments */}
        <div className="border-t border-border pt-3 space-y-2">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Payments</Label>
          {(inv.payments || []).map((p) => (
            <div key={p.id} className="flex items-center justify-between text-sm bg-muted/40 rounded px-3 py-1.5" data-testid={`invoice-payment-${p.id}`}>
              <span>{p.date} · {p.method}</span>
              <span className="flex items-center gap-3"><span className="font-mono">{money(p.amount)}</span><button onClick={() => rmPayment(p.id)} className="text-red-500"><Trash2 className="h-3.5 w-3.5" /></button></span>
            </div>
          ))}
          <div className="flex items-end gap-2 flex-wrap">
            <div><Label className="text-xs">Amount</Label><Input className="w-28" type="number" value={pay.amount} onChange={(e) => setPay((o) => ({ ...o, amount: e.target.value }))} data-testid="invoice-payment-amount" /></div>
            <div><Label className="text-xs">Method</Label>
              <Select value={pay.method} onValueChange={(v) => setPay((o) => ({ ...o, method: v }))}>
                <SelectTrigger className="w-32" data-testid="invoice-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>{["Cash", "Card", "E-transfer", "Cheque", "Deposit", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Date</Label><Input className="w-44" type="date" value={pay.date} onChange={(e) => setPay((o) => ({ ...o, date: e.target.value }))} /></div>
            <Button variant="outline" onClick={addPayment} data-testid="invoice-add-payment"><Plus className="h-4 w-4 mr-1" />Record payment</Button>
          </div>
        </div>

        <div><Label className="text-xs">Notes</Label><Textarea rows={2} value={inv.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="invoice-notes" /></div>

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button variant="outline" onClick={print} data-testid="invoice-print"><Printer className="h-4 w-4 mr-1.5" />Download / Print PDF</Button>
          <Button onClick={() => save(false)} disabled={saving} data-testid="invoice-save"><Save className="h-4 w-4 mr-1.5" />{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
